import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tag, Plus, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDevelopmentTags = () => {
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // tag or {} for new
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/development-tags`, { params: { include_inactive: true } });
      setTags(res.data.tags || []);
      setCategories(res.data.categories || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load development tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    tags.forEach((t) => { (g[t.category] = g[t.category] || []).push(t); });
    return g;
  }, [tags]);

  const save = async () => {
    if (!editing.label?.trim() || !editing.category) { toast.error('Category and label are required'); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/development-tags/${editing.id}`, { label: editing.label, category: editing.category, active: editing.active });
        toast.success('Tag updated');
      } else {
        await axios.post(`${API}/development-tags`, { label: editing.label, category: editing.category, active: editing.active !== false });
        toast.success('Tag created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (tag) => {
    try {
      await axios.put(`${API}/development-tags/${tag.id}`, { active: !tag.active });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update tag');
    }
  };

  const catList = categories.length ? categories : Object.keys(grouped);

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-4 py-6" data-testid="admin-development-tags-page">
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2"><Tag className="h-6 w-6 text-emerald-600" /> Development Tags</h1>
            <p className="mt-0.5 text-sm text-slate-500">Manage the canonical PFAP development-tag taxonomy used in Match Analysis. Once a tag is used historically its label/category are locked; deactivate instead of renaming.</p>
          </div>
          <Button onClick={() => setEditing({ category: catList[0] || '', label: '', active: true })} data-testid="new-tag-btn"><Plus className="mr-1.5 h-4 w-4" /> New Tag</Button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-6">
            {catList.map((cat) => (
              <div key={cat}>
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{cat}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(grouped[cat] || []).sort((a, b) => a.label.localeCompare(b.label)).map((t) => (
                    <div key={t.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${t.active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'}`} data-testid={`tag-row-${t.id}`}>
                      <span className={`text-sm ${t.active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{t.label}</span>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setEditing({ ...t })} className="text-slate-400 hover:text-slate-700" data-testid={`tag-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        <button
                          onClick={() => toggleActive(t)}
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${t.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}
                          data-testid={`tag-toggle-${t.id}`}
                        >
                          {t.active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </div>
                  ))}
                  {(grouped[cat] || []).length === 0 && <div className="text-xs text-slate-400">No tags in this category.</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="tag-edit-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit Development Tag' : 'New Development Tag'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-1">
              <div>
                <Label className="mb-1.5 block">Category</Label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="tag-category-select">
                  {catList.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="mb-1.5 block">Label</Label>
                <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="e.g. Scanning" data-testid="tag-label-input" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="tag-active-checkbox" /> Active
              </label>
              {editing.id && <p className="text-xs text-slate-400">If this tag is already used in observations, its label &amp; category cannot be changed — only its active state.</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="tag-save-btn">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminDevelopmentTags;
